import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Lock, Clock, CheckCircle2, PlayCircle, XCircle, MessageSquarePlus, Ban, Trash2, RotateCcw, Download, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AgingChip } from "@/components/AgingChip";
import StageBadge from "@/components/StageBadge";
import ReferralFormDialog from "@/components/ReferralFormDialog";
import PendingForm from "@/components/PendingForm";
import OwnershipPanel, { laneState } from "@/components/OwnershipPanel";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const MAX_ATTACH_SIZE = 15 * 1024 * 1024;
const MAX_ATTACH_FILES = 20;
const ALLOWED_ATTACH_EXT = ["pdf", "png", "jpg", "jpeg", "gif", "webp", "csv", "txt", "doc", "docx", "xls", "xlsx"];

function Row({ label, value, mono, keep }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-white/5 last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground font-mono">{label}</span>
      <span className={`text-sm text-right ${mono ? "font-mono" : ""} ${keep || ""}`}>{value ?? "—"}</span>
    </div>
  );
}

export default function ReferralDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ref, setRef] = useState(null);
  const [config, setConfig] = useState(null);
  const [log, setLog] = useState([]);
  const idRef = useRef(id);
  idRef.current = id;
  const [editOpen, setEditOpen] = useState(false);

  const canManage = ["coordinator", "manager", "admin"].includes(user?.role);
  const canPHI = ["coordinator", "admin", "manager"].includes(user?.role);
  const isAdmin = user?.role === "admin";
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [selAtt, setSelAtt] = useState([]);
  const [deletingAtt, setDeletingAtt] = useState(false);
  const [zipping, setZipping] = useState(false);
  const [renaming, setRenaming] = useState({});
  const [attPreviews, setAttPreviews] = useState({});
  const [working, setWorking] = useState(false);
  const [notAdmitOpen, setNotAdmitOpen] = useState(false);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const addNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await api.put(`/referrals/${id}`, { note: noteText.trim() });
      setNoteText("");
      toast.success("Note added");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add note");
    } finally { setSavingNote(false); }
  };

  const act = async (path, body, msg = "Referral updated") => {
    setWorking(true);
    try {
      await api.post(`/referrals/${id}/${path}`, body);
      toast.success(msg);
      setNotAdmitOpen(false); setPendingOpen(false); setReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Action failed");
    } finally { setWorking(false); }
  };

  const cancelReferral = async () => {
    const r = window.prompt("Cancel this referral? It will be hidden from lists & analytics and can be restored later. Add an optional reason:", "");
    if (r === null) return;
    try {
      await api.post(`/referrals/${id}/cancel`, { reason: r });
      toast.success("Referral cancelled");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel"); }
  };
  const restoreReferral = async () => {
    try { await api.post(`/referrals/${id}/restore`); toast.success("Referral restored"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not restore"); }
  };
  const deleteReferral = async () => {
    if (!window.confirm(`Permanently DELETE ${id}? This removes the record and its attachments for good — it cannot be undone.`)) return;
    try {
      await api.delete(`/referrals/${id}`);
      toast.success(`${id} deleted`);
      navigate("/referrals");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); }
  };

  const processFiles = async (rawList) => {
    let selected = Array.from(rawList || []);
    if (!selected.length) return;
    const badType = selected.filter((f) => !ALLOWED_ATTACH_EXT.includes((f.name.split(".").pop() || "").toLowerCase()));
    badType.forEach((f) => toast.error(`${f.name}: unsupported file type — skipped`));
    selected = selected.filter((f) => ALLOWED_ATTACH_EXT.includes((f.name.split(".").pop() || "").toLowerCase()));
    const tooBig = selected.filter((f) => f.size > MAX_ATTACH_SIZE);
    tooBig.forEach((f) => toast.error(`${f.name} exceeds 15 MB — skipped`));
    selected = selected.filter((f) => f.size <= MAX_ATTACH_SIZE);
    if (selected.length > MAX_ATTACH_FILES) {
      toast.error(`Up to ${MAX_ATTACH_FILES} files at once — keeping the first ${MAX_ATTACH_FILES}`);
      selected = selected.slice(0, MAX_ATTACH_FILES);
    }
    if (!selected.length) return;
    setUploading(true);
    let ok = 0;
    for (let i = 0; i < selected.length; i++) {
      const f = selected[i];
      setUploadMsg(`Uploading ${i + 1}/${selected.length}…`);
      try {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/referrals/${id}/attachments`, fd);
        ok++;
      } catch (err) {
        toast.error(`${f.name}: ${err.response?.data?.detail || "upload failed"}`);
      }
    }
    setUploading(false);
    setUploadMsg("");
    if (ok) toast.success(`${ok} file${ok > 1 ? "s" : ""} attached`);
    load();
  };

  const uploadFiles = (e) => {
    const files = e.target.files;
    e.target.value = "";
    processFiles(files);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const dropped = e.dataTransfer?.files;
    if (dropped?.length) processFiles(dropped);
  };

  const toggleSelAtt = (attId) =>
    setSelAtt((s) => (s.includes(attId) ? s.filter((x) => x !== attId) : [...s, attId]));

  const bulkDeleteAtt = async () => {
    if (!selAtt.length) return;
    if (!window.confirm(`Permanently delete ${selAtt.length} attachment(s)? This cannot be undone.`)) return;
    setDeletingAtt(true);
    try {
      const { data } = await api.post(`/referrals/${id}/attachments/bulk-delete`, { ids: selAtt });
      toast.success(`${data.deleted} attachment(s) deleted`);
      setSelAtt([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingAtt(false);
    }
  };

  const bulkDownloadAtt = async () => {
    if (!selAtt.length) return;
    setZipping(true);
    try {
      const res = await api.post(`/referrals/${id}/attachments/bulk-download`, { ids: selAtt }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${id}_attachments.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    } finally {
      setZipping(false);
    }
  };

  const startRename = (att) => setRenaming((r) => ({ ...r, [att.id]: att.filename || "" }));
  const cancelRename = (attId) => setRenaming((r) => { const n = { ...r }; delete n[attId]; return n; });
  const saveRename = async (attId) => {
    const name = (renaming[attId] || "").trim();
    if (!name) { toast.error("Filename cannot be empty"); return; }
    try {
      await api.patch(`/referrals/${id}/attachments/${attId}`, { filename: name });
      toast.success("Attachment renamed");
      cancelRename(attId);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await api.get(`/referrals/${id}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename || att.id; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const canPreview = (att) => {
    const ct = att.content_type || "";
    return ct.startsWith("image/") || ct === "application/pdf";
  };

  const togglePreview = async (att) => {
    const cur = attPreviews[att.id];
    if (cur?.url) {
      setAttPreviews((p) => ({ ...p, [att.id]: { ...cur, open: !cur.open } }));
      return;
    }
    setAttPreviews((p) => ({ ...p, [att.id]: { open: true, loading: true } }));
    try {
      const res = await api.get(`/referrals/${id}/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setAttPreviews((p) => ({ ...p, [att.id]: { open: true, url } }));
    } catch {
      toast.error("Preview failed");
      setAttPreviews((p) => ({ ...p, [att.id]: { open: false } }));
    }
  };

  useEffect(() => () => {
    Object.values(attPreviews).forEach((v) => v?.url && URL.revokeObjectURL(v.url));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async () => {
    const cur = id;
    const r = await api.get(`/referrals/${cur}`);
    if (idRef.current !== cur) { return; }
    setRef(r.data);
    if (canManage) {
      api.get("/audit-log", { params: { referral_id: cur } })
        .then((l) => { if (idRef.current === cur) { setLog(l.data); } })
        .catch(() => setLog([]));
    } else {
      setLog([]);
    }
  };
  useEffect(() => { api.get("/config").then((r) => setConfig(r.data)); load(); }, [id]);

  if (!ref) return <div className="p-6 font-mono text-sm text-muted-foreground">Loading {id}…</div>;
  const isPending = ref.status === "Pending Disposition";
  const laneLock = laneState(ref, user).blocked;

  return (
    <div className="p-6 space-y-4 max-w-5xl" data-testid="referral-detail-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground font-mono" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> back
      </button>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-head font-black text-3xl tracking-tight font-mono text-cyan-300">{ref.id}</h1>
            <StageBadge referral={ref} days={ref.aging.is_open ? ref.aging.business_days_open : null} />
            <AgingChip severity={ref.aging.severity} stale={ref.aging.stale} days={ref.aging.is_open ? ref.aging.calendar_days_since_note : null} />
            {ref.out_of_summary && <span className="text-xs font-mono text-amber-400 border border-amber-500/40 rounded px-2 py-0.5">OUT OF SUMMARY</span>}
          </div>
          <p className="text-sm text-muted-foreground font-mono mt-1.5">{ref.lane} · {ref.aging.is_open ? `${ref.aging.business_days_open} biz-days open` : `cycle ${ref.aging.cycle_days_biz ?? "—"} biz-days`}</p>
        </div>
        {canManage && !laneLock && (
          <Button onClick={() => setEditOpen(true)} data-testid="edit-referral-btn" className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold gap-2">
            <Pencil className="w-4 h-4" /> Edit / Change Status
          </Button>
        )}
      </div>

      {ref.cancelled && (
        <div className="glass rounded-xl p-3 border border-red-500/30 flex items-center gap-2" data-testid="cancelled-banner">
          <Ban className="w-4 h-4 text-red-400 shrink-0" />
          <span className="text-sm text-red-300">This referral is <b>cancelled</b>{ref.cancel_reason ? ` — ${ref.cancel_reason}` : ""}. It's hidden from lists &amp; analytics{ref.cancelled_by ? ` · by ${ref.cancelled_by}` : ""}.</span>
        </div>
      )}

      <OwnershipPanel referral={ref} user={user} onChanged={load} />

      {canManage && ref.aging.is_open && !laneLock && (
        <div className="glass glass-cyan rounded-xl p-4" data-testid="workflow-bar">
          <h2 className="font-head font-bold text-sm uppercase tracking-wide text-cyan-300 mb-3">Workflow · one-tap progression</h2>
          <p className="text-xs text-cyan-200/70 -mt-2 mb-3" data-testid="workflow-autonotify-hint">
            Each update automatically emails the source that logged this referral — no manual step needed.
          </p>
          {ref.status === "New" && (
            <div className="flex items-center gap-3 flex-wrap">
              <p className="text-sm text-muted-foreground">Awaiting confirmation of receipt{ref.lane_contacts?.length > 1 ? " from anyone in the lane" : ""}.</p>
              <Button onClick={() => act("confirm")} disabled={working} data-testid="workflow-confirm"
                      className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold gap-2">
                <CheckCircle2 className="w-4 h-4" /> Confirm receipt
              </Button>
            </div>
          )}
          {["In-progress", "Monitoring", "On-hold", "Pending Disposition"].includes(ref.status) && (
            <div className="space-y-3">
              {isPending ? (
                <div className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${ref.aging.followup_overdue ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-orange-500/30 bg-orange-500/5 text-orange-300"}`} data-testid="pending-info">
                  <CalendarClock className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    Pending on <b data-testid="pending-info-reason">{ref.pending_reason || "—"}</b> · follow-up <b className="font-mono" data-testid="pending-info-date">{fmtDate(ref.recheck_date)}</b>
                    {ref.aging.followup_overdue && <span data-testid="pending-info-overdue"> · {ref.aging.followup_overdue_days}d overdue</span>}
                    {ref.aging.severity === "yellow" && <span> · due today</span>}
                  </span>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Confirmed{ref.confirmed_by ? ` by ${ref.confirmed_by}` : ""}. Record the outcome when known.</p>
              )}
              {pendingOpen ? (
                <PendingForm reasons={config?.pending_reasons} busy={working} withNote initialReason={isPending ? ref.pending_reason || "" : ""}
                             submitLabel={isPending ? "Save new follow-up" : "Mark pending"}
                             onSubmit={(body) => act("pending", body, `Marked pending — follow-up ${fmtDate(body.followup_date)}`)} onCancel={() => setPendingOpen(false)} />
              ) : !notAdmitOpen ? (
                <div className="flex items-center gap-3 flex-wrap">
                  <Button onClick={() => act("admit")} disabled={working} data-testid="workflow-admit"
                          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold gap-2">
                    <PlayCircle className="w-4 h-4" /> Admit / Start of Care
                  </Button>
                  <Button onClick={() => setNotAdmitOpen(true)} variant="outline" data-testid="workflow-notadmit"
                          className="border-white/15 gap-2"><XCircle className="w-4 h-4" /> Not admitted</Button>
                  <Button onClick={() => setPendingOpen(true)} variant="outline" data-testid="workflow-pending"
                          className="border-orange-500/40 text-orange-300 hover:text-orange-200 gap-2">
                    <CalendarClock className="w-4 h-4" /> {isPending ? "Still pending · new date" : "Pending – follow-up needed"}
                  </Button>
                </div>
              ) : (
                <div className="flex items-end gap-3 flex-wrap" data-testid="workflow-reason-block">
                  <div className="space-y-1">
                    <span className="text-xs uppercase tracking-wide text-muted-foreground font-mono">Reason</span>
                    <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="workflow-reason-select"
                            className="block h-9 rounded-md bg-slate-900/60 border border-white/10 px-2 text-sm min-w-[200px]">
                      <option value="">Select a reason…</option>
                      {(config?.reason_codes || []).map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <Button onClick={() => act("not-admitted", { reason_code: reason })} disabled={working || !reason} data-testid="workflow-notadmit-submit"
                          className="bg-slate-200 hover:bg-white text-slate-950 font-semibold">Submit</Button>
                  <button onClick={() => setNotAdmitOpen(false)} className="text-xs font-mono text-muted-foreground hover:text-foreground pb-2">cancel</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div className="glass rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-2">
            <Lock className="w-3.5 h-3.5 text-emerald-400" />
            <h2 className="font-head font-bold text-sm uppercase tracking-wide text-emerald-300">Protected Record (PHI)</h2>
          </div>
          {canPHI ? (
            <>
              <Row label="Patient Name" value={ref.patient_name} keep="text-foreground" />
              <Row label="MRN" value={ref.mrn} mono />
            </>
          ) : (
            <div className="text-xs font-mono text-amber-400/80 py-2 border-b border-white/5" data-testid="phi-hidden">Patient name &amp; MRN hidden for your role (initials + ID only)</div>
          )}
          <Row label="Initials (shared)" value={ref.initials} mono />
          <Row label="Source Line" value={ref.source_line} />
          <Row label="Receiving Line" value={ref.receiving_line} keep={ref.out_of_summary ? "text-amber-300" : ""} />
          <Row label="Referral Source" value={ref.referral_source} />
          <Row label="Location" value={ref.location} />
          <Row label="Receiving Contact" value={ref.receiving_contact || (ref.lane_contacts?.length ? `Unclaimed · ${ref.lane_contacts.length} lane contacts` : "UNASSIGNED")} keep={ref.receiving_contact ? "" : "text-amber-400"} />
        </div>

        <div className="glass rounded-xl p-4">
          <h2 className="font-head font-bold text-sm uppercase tracking-wide text-muted-foreground mb-2">Dates & Disposition</h2>
          <Row label="Refer Date" value={fmtDate(ref.refer_date)} mono />
          <Row label="Confirmed" value={ref.confirmed_at ? `${fmtDateTime(ref.confirmed_at)}${ref.confirmed_by ? ` · ${ref.confirmed_by}` : ""}` : "—"} keep={ref.confirmed_at ? "text-emerald-300" : ""} />
          <Row label={isPending ? "Follow-up Date" : "Re-check Date"} value={ref.recheck_date ? fmtDate(ref.recheck_date) : "—"} mono keep={isPending && ref.aging.followup_overdue ? "text-red-400" : ""} />
          {isPending && <Row label="Pending On" value={ref.pending_reason || "—"} keep="text-orange-300" />}
          <Row label="Owner" value={ref.owner || "—"} />
          <Row label="Close Date" value={ref.close_date ? fmtDate(ref.close_date) : "—"} mono />
          <Row label="Reason Code" value={ref.reason_code || "—"} />
          <Row label="Completion (SOC/Admit)" value={ref.completion_type || "—"} keep={ref.status === "Completed" && !ref.completion_type ? "text-yellow-400" : ""} />
          <Row label="Last Note" value={ref.last_note_date ? fmtDate(ref.last_note_date) : "—"} mono />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="glass rounded-xl p-4">
          <h2 className="font-head font-bold text-sm uppercase tracking-wide text-muted-foreground mb-3">Notes</h2>
          {canManage && (
            <div className="mb-3 space-y-2" data-testid="add-note-block">
              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2}
                        placeholder="Add a note… (also resets the aging clock)" data-testid="add-note-input"
                        className="bg-slate-900/60 border-white/10 text-sm" />
              <Button onClick={addNote} disabled={savingNote || !noteText.trim()} data-testid="add-note-btn"
                      className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold gap-2 h-8 text-xs">
                <MessageSquarePlus className="w-3.5 h-3.5" /> {savingNote ? "Adding…" : "Add note"}
              </Button>
            </div>
          )}
          <div className="space-y-2">
            {(ref.notes || []).length === 0 && <p className="text-xs text-muted-foreground font-mono">{canPHI ? "No notes logged." : "Notes hidden for your role."}</p>}
            {(ref.notes || []).slice().reverse().map((n, i) => (
              <div key={i} className="text-sm border-l-2 border-cyan-500/40 pl-3 py-0.5">
                <p>{n.text}</p>
                <p className="text-[11px] text-muted-foreground font-mono mt-0.5">{n.author} · {fmtDateTime(n.at)}</p>
              </div>
            ))}
          </div>
        </div>

        {canManage && (
        <div className="glass rounded-xl p-4">
          <div className="flex items-center gap-1.5 mb-3">
            <Clock className="w-3.5 h-3.5 text-violet-400" />
            <h2 className="font-head font-bold text-sm uppercase tracking-wide text-muted-foreground">Audit Trail</h2>
          </div>
          <div className="space-y-2" data-testid="referral-audit">
            {log.length === 0 && <p className="text-xs text-muted-foreground font-mono">No entries.</p>}
            {log.map((e, i) => (
              <div key={i} className="text-xs border-l-2 border-violet-500/40 pl-3 py-0.5 font-mono">
                <span className="text-violet-300 uppercase">{e.action}</span> by {e.user_name} <span className="text-muted-foreground">({e.role})</span>
                <div className="text-[11px] text-muted-foreground">{fmtDateTime(e.at)}</div>
                {e.changes && Object.keys(e.changes).length > 0 && (
                  <div className="text-[11px] text-slate-400 mt-0.5">
                    {Object.entries(e.changes).map(([k, v]) => <span key={k} className="mr-2">{k}: {String(v.from ?? "∅")}→{String(v.to ?? "∅")}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
        )}
      </div>

      <ReferralFormDialog open={editOpen} onOpenChange={setEditOpen} config={config} initial={ref} onSaved={load} />

      {canPHI && (
        <div className={`glass rounded-xl p-4 transition-colors ${dragOver ? "ring-2 ring-cyan-400 bg-cyan-400/5" : ""}`} data-testid="attachments-card"
             onDragOver={(e) => { e.preventDefault(); if (!uploading) setDragOver(true); }}
             onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
             onDrop={onDrop}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-head font-bold text-sm uppercase tracking-wide text-muted-foreground">Attachments (PHI)</h2>
            <div className="flex items-center gap-2">
              {isAdmin && selAtt.length > 0 && (
                <>
                  <button onClick={bulkDownloadAtt} disabled={zipping} data-testid="attachment-bulk-download-btn"
                          className="inline-flex items-center gap-1.5 rounded-md bg-white/8 hover:bg-white/15 text-foreground border border-white/15 font-semibold px-3 py-1.5 text-xs transition-colors disabled:opacity-60">
                    <Download size={13} /> {zipping ? "Zipping…" : `Download ZIP (${selAtt.length})`}
                  </button>
                  <button onClick={bulkDeleteAtt} disabled={deletingAtt} data-testid="attachment-bulk-delete-btn"
                          className="inline-flex items-center gap-1.5 rounded-md bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 font-semibold px-3 py-1.5 text-xs transition-colors disabled:opacity-60">
                    <Trash2 size={13} /> {deletingAtt ? "Deleting…" : `Delete selected (${selAtt.length})`}
                  </button>
                </>
              )}
              <label className={`inline-flex items-center gap-2 rounded-md bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold px-3 py-1.5 text-xs transition-colors ${uploading ? "opacity-60 cursor-wait" : "cursor-pointer"}`} data-testid="attachment-upload-label">
                {uploading ? (uploadMsg || "Uploading…") : "Upload files"}
                <input type="file" multiple className="hidden" onChange={uploadFiles} disabled={uploading} data-testid="attachment-input"
                       accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.csv,.txt,.doc,.docx,.xls,.xlsx" />
              </label>
            </div>
          </div>
          <div className="space-y-1.5">
            {isAdmin && (ref.attachments || []).length > 1 && (
              <label className="flex items-center gap-2 pb-1.5 text-[11px] font-mono text-muted-foreground cursor-pointer w-fit" data-testid="attachment-select-all">
                <input type="checkbox"
                       checked={selAtt.length === (ref.attachments || []).length && selAtt.length > 0}
                       onChange={() => setSelAtt(selAtt.length === (ref.attachments || []).length ? [] : (ref.attachments || []).map((x) => x.id))}
                       className="h-3.5 w-3.5 accent-cyan-400 cursor-pointer" />
                Select all
              </label>
            )}
            {(ref.attachments || []).length === 0 && <p className="text-xs text-muted-foreground font-mono" data-testid="attachments-empty">No attachments. Drag files here or use “Upload files”.</p>}
            {(ref.attachments || []).map((a) => {
              const prev = attPreviews[a.id];
              return (
                <div key={a.id} className="rounded-md border border-white/8 overflow-hidden" data-testid={`attachment-${a.id}`}>
                  <div className="flex items-center justify-between gap-2 px-2 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      {isAdmin && (
                        <input type="checkbox" checked={selAtt.includes(a.id)} onChange={() => toggleSelAtt(a.id)}
                               data-testid={`attachment-select-${a.id}`}
                               className="h-3.5 w-3.5 shrink-0 accent-cyan-400 cursor-pointer" />
                      )}
                      {renaming[a.id] !== undefined ? (
                        <span className="flex items-center gap-1.5 min-w-0">
                          <input value={renaming[a.id]} autoFocus
                                 onChange={(e) => setRenaming((r) => ({ ...r, [a.id]: e.target.value }))}
                                 onKeyDown={(e) => { if (e.key === "Enter") saveRename(a.id); if (e.key === "Escape") cancelRename(a.id); }}
                                 data-testid={`attachment-rename-input-${a.id}`}
                                 className="h-7 w-48 bg-slate-900/60 border border-white/10 rounded px-2 text-sm" />
                          <button onClick={() => saveRename(a.id)} data-testid={`attachment-rename-save-${a.id}`} className="text-xs font-mono text-cyan-300 hover:text-cyan-200">Save</button>
                          <button onClick={() => cancelRename(a.id)} className="text-xs font-mono text-muted-foreground hover:text-foreground">Cancel</button>
                        </span>
                      ) : (
                        <>
                          <span className="text-sm text-cyan-300 truncate">{a.filename}</span>
                          <button onClick={() => startRename(a)} data-testid={`attachment-rename-${a.id}`} title="Rename"
                                  className="text-muted-foreground hover:text-cyan-300 shrink-0"><Pencil size={12} /></button>
                        </>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0 ml-2">
                      <span className="text-[11px] font-mono text-muted-foreground">{Math.round((a.size || 0) / 1024)} KB</span>
                      {canPreview(a) && (
                        <button onClick={() => togglePreview(a)} data-testid={`attachment-preview-${a.id}`}
                                className="text-xs font-mono text-cyan-300 hover:text-cyan-200">
                          {prev?.open ? "Hide" : "Preview"}
                        </button>
                      )}
                      <button onClick={() => downloadAttachment(a)} data-testid={`attachment-download-${a.id}`}
                              className="text-xs font-mono text-muted-foreground hover:text-foreground">Download</button>
                    </div>
                  </div>
                  {prev?.open && (
                    <div className="border-t border-white/8 bg-black/20 p-2" data-testid={`attachment-view-${a.id}`}>
                      {prev.loading && <p className="text-xs text-muted-foreground font-mono py-6 text-center">Loading preview…</p>}
                      {prev.url && a.content_type === "application/pdf" && (
                        <iframe title={a.filename} src={prev.url} className="w-full h-[520px] rounded bg-white" data-testid={`attachment-frame-${a.id}`} />
                      )}
                      {prev.url && (a.content_type || "").startsWith("image/") && (
                        <img alt={a.filename} src={prev.url} className="max-h-[520px] w-auto mx-auto rounded" data-testid={`attachment-img-${a.id}`} />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isAdmin && (
        <div className="glass rounded-xl p-4 border border-red-500/20" data-testid="admin-actions">
          <h2 className="font-head font-bold text-sm uppercase tracking-wide text-red-300 mb-3">Admin actions</h2>
          <div className="flex items-center gap-3 flex-wrap">
            {ref.cancelled ? (
              <Button onClick={restoreReferral} data-testid="restore-referral-btn" variant="outline" className="border-white/15 gap-2">
                <RotateCcw className="w-4 h-4" /> Restore referral
              </Button>
            ) : (
              <Button onClick={cancelReferral} data-testid="cancel-referral-btn" variant="outline" className="border-amber-500/40 text-amber-300 hover:text-amber-200 gap-2">
                <Ban className="w-4 h-4" /> Cancel referral
              </Button>
            )}
            <Button onClick={deleteReferral} data-testid="delete-referral-btn" className="bg-red-500/90 hover:bg-red-500 text-white font-semibold gap-2">
              <Trash2 className="w-4 h-4" /> Delete permanently
            </Button>
          </div>
          <p className="text-xs text-muted-foreground font-mono mt-2">Cancel = reversible soft-void (hidden from lists &amp; analytics, restorable). Delete = permanent removal of the record + attachments.</p>
        </div>
      )}
    </div>
  );
}
